/**
 * CLI sign-in approval, mounted at /cli.
 *
 * The `ploy` command line tool prints a short code and waits. This page is
 * where that code becomes a sign-in: the member types it, sees who asked, and
 * approves or denies. Without this page the flow has no end.
 *
 * ONE RULE ABOVE ALL: the code is never read from the URL. Not from a query
 * parameter, not from a path segment, not from a paste of a link. The grant
 * accepts one attack by design, somebody else starts a sign-in and talks a
 * member into approving it, and the only thing standing in the way is that a
 * person has to read a code off their own terminal and type it in. A page that
 * pre-filled the code from a link would turn that attack back into one click.
 * If you are ever tempted to add `useSearchParams` here, that is the reason
 * not to, and there is a test that fails if you do.
 *
 * The evidence is the content, not decoration. A member who can see the
 * address, the client and the time has something concrete to disagree with;
 * "Approve?" on its own would make approval a reflex.
 */
import { Box, Flex, Heading, Stack, Text } from "@chakra-ui/react";
import { IconType } from "react-icons";
import { TbAlertTriangle, TbCheck, TbClock, TbTerminal2, TbX } from "react-icons/tb";
import { useEffect, useRef, useState } from "react";

import Card from "components/ds/Card";
import MFMembers from "state/members/membersState";
import {
  formatUserCode,
  isCompleteUserCode,
  useCliDeviceApprove,
  useCliDeviceDeny,
  useCliDeviceLookup,
  type CliDeviceAttempt,
  type CliDeviceFailure,
} from "state/members/cliDeviceApproval";

import ApprovalDetails from "./ApprovalDetails";
import CodeForm from "./CodeForm";
import Outcome from "./Outcome";
import { hasExpired } from "./expiry";

type Stage =
  | { name: "enter" }
  | {
      name: "confirm";
      attempt: CliDeviceAttempt;
      /**
       * Whether this browser's clock agrees with the server's.
       *
       * The server only ever describes an attempt that is still pending, so if
       * `expiresAt` is already in the past by the time the answer lands, it is
       * this machine's clock that is wrong, not the code. Counting down against
       * it would kill a perfectly good code on sight and print nonsense next to
       * the time it was asked for, so when it disagrees the page says less
       * rather than something untrue.
       */
      clockTrusted: boolean;
    }
  | { name: "approved" }
  | { name: "denied" }
  | { name: "expired" }
  /** Gone before the member acted: expired server side, or already decided. */
  | { name: "unavailable"; message: string };

type Tone = "p" | "success" | "error" | "warning";

const INCOMPLETE_CODE_MESSAGE =
  "A code is eight characters, like WXYZ-2345. Check your terminal and type the whole code.";

const StatusBadge = ({ tone, icon }: { tone: Tone; icon: IconType }) => (
  <Flex
    align="center"
    justify="center"
    boxSize="40px"
    borderRadius="full"
    bg={`${tone}.50`}
    color={`${tone}.600`}
    flexShrink={0}
  >
    <Box as={icon} boxSize="20px" aria-hidden />
  </Flex>
);

const CliApproval = () => {
  const me = MFMembers.useGetMe();
  const [code, setCode] = useState("");
  const [stage, setStage] = useState<Stage>({ name: "enter" });
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());

  const inputRef = useRef<HTMLInputElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  const lookup = useCliDeviceLookup();
  const approve = useCliDeviceApprove();
  const deny = useCliDeviceDeny();

  // The countdown runs off a local clock. Asking the server how long is left
  // would spend one of the ten tries a minute the member may still need.
  useEffect(() => {
    if (stage.name !== "confirm") return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [stage.name]);

  // An attempt can run out while it is being read. Approve would fail anyway,
  // so the buttons go rather than leaving a member to press one and be told no.
  useEffect(() => {
    if (stage.name !== "confirm" || !stage.clockTrusted) return;
    if (hasExpired(stage.attempt.expiresAt, now)) {
      setError(null);
      setStage({ name: "expired" });
    }
  }, [stage, now]);

  // Move focus with the step, so a keyboard or screen reader lands on the new
  // content instead of staying on a button that no longer exists.
  useEffect(() => {
    if (stage.name === "enter") inputRef.current?.focus();
    else headingRef.current?.focus();
  }, [stage.name]);

  const submitCode = () => {
    setError(null);
    if (!isCompleteUserCode(code)) {
      setError(INCOMPLETE_CODE_MESSAGE);
      return;
    }
    lookup.mutate(code, {
      onSuccess: (attempt) => {
        const arrivedAt = Date.now();
        setNow(arrivedAt);
        setStage({
          name: "confirm",
          attempt,
          clockTrusted: !hasExpired(attempt.expiresAt, arrivedAt),
        });
      },
      onError: (failure) => setError(failure.message),
    });
  };

  // A 404 on either decision is terminal: the attempt has expired, or somebody
  // has already approved or denied it. Anything else (rate limit, network,
  // session) leaves the decision on screen so it can be tried again.
  const onDecisionError = (failure: CliDeviceFailure) => {
    if (failure.status === 404) {
      setStage({ name: "unavailable", message: failure.message });
    } else {
      setError(failure.message);
    }
  };

  const decide = (choice: "approve" | "deny") => {
    if (stage.name !== "confirm") return;
    setError(null);
    const { userCode } = stage.attempt;

    if (choice === "approve") {
      approve.mutate(userCode, {
        onSuccess: () => setStage({ name: "approved" }),
        onError: onDecisionError,
      });
    } else {
      deny.mutate(userCode, {
        onSuccess: () => setStage({ name: "denied" }),
        onError: onDecisionError,
      });
    }
  };

  const startOver = () => {
    setCode("");
    setError(null);
    lookup.reset();
    approve.reset();
    deny.reset();
    setStage({ name: "enter" });
  };

  const view = ((): {
    title: string;
    subtitle?: string;
    tone: Tone;
    icon: IconType;
    body: JSX.Element;
  } => {
    switch (stage.name) {
      case "confirm":
        return {
          title: "Approve this sign-in?",
          tone: "p",
          icon: TbTerminal2,
          body: (
            <ApprovalDetails
              attempt={stage.attempt}
              memberEmail={me?.email}
              now={now}
              clockTrusted={stage.clockTrusted}
              onApprove={() => decide("approve")}
              onDeny={() => decide("deny")}
              onStartOver={startOver}
              isApproving={approve.isLoading}
              isDenying={deny.isLoading}
              error={error}
            />
          ),
        };

      case "approved":
        return {
          title: "Sign-in approved",
          tone: "success",
          icon: TbCheck,
          body: (
            <Outcome
              lines={[
                "Your terminal is signed in. You can close this page and go back to it.",
                "That machine stays signed in as you for up to 30 days.",
              ]}
              actionLabel="Approve another code"
              onAction={startOver}
            />
          ),
        };

      case "denied":
        return {
          title: "Sign-in denied",
          tone: "error",
          icon: TbX,
          body: (
            <Outcome
              lines={[
                "Nothing was signed in, and that code cannot be used now.",
                "If you did not start this yourself, tell your IT or security team what you saw.",
              ]}
              actionLabel="Type another code"
              onAction={startOver}
            />
          ),
        };

      case "expired":
        return {
          title: "That code has expired",
          tone: "warning",
          icon: TbClock,
          body: (
            <Outcome
              lines={[
                "Codes last ten minutes. Nothing was signed in.",
                "Start the sign-in again in your terminal to get a new code.",
              ]}
              actionLabel="Type another code"
              onAction={startOver}
            />
          ),
        };

      case "unavailable":
        return {
          title: "That code cannot be used",
          tone: "warning",
          icon: TbAlertTriangle,
          body: (
            <Outcome
              lines={[
                stage.message,
                "If you did not expect that, someone else may have acted on it already. Start the sign-in again in your terminal to get a new code.",
              ]}
              actionLabel="Type another code"
              onAction={startOver}
            />
          ),
        };

      default:
        return {
          title: "Approve a terminal sign-in",
          subtitle:
            "The ploy command line tool shows a code when you sign in. Type that code here to finish signing in.",
          tone: "p",
          icon: TbTerminal2,
          body: (
            <CodeForm
              ref={inputRef}
              code={code}
              onCodeChange={(next) => setCode(formatUserCode(next))}
              onSubmit={submitCode}
              isLoading={lookup.isLoading}
              error={error}
            />
          ),
        };
    }
  })();

  return (
    <Box maxW="560px" mx="auto" w="full" py="lg">
      <Card>
        <Stack spacing="lg">
          <Flex gap="md" align="center">
            <StatusBadge tone={view.tone} icon={view.icon} />
            <Heading
              as="h1"
              ref={headingRef}
              tabIndex={-1}
              fontSize="xl"
              fontWeight={600}
              color="neutral.950"
              _focus={{ outline: "none" }}
            >
              {view.title}
            </Heading>
          </Flex>

          {view.subtitle && (
            <Text fontSize="smd" color="neutral.700">
              {view.subtitle}
            </Text>
          )}

          {view.body}
        </Stack>
      </Card>

      {stage.name === "enter" && (
        <Text fontSize="xs" color="neutral.700" mt="lg" px="2xs">
          A code should only ever come from a terminal you are sitting at. If
          someone sent you one, or asked you to read one out, do not type it in.
        </Text>
      )}
    </Box>
  );
};

export default CliApproval;
